import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { ArrowLeft, Loader2, Info, Check, Zap, Settings, User, FileText, Workflow, MessageSquare } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent } from "../components/ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { MultiSelect } from "../components/ui/MultiSelect";
import { TagManagement, TagManagementRef } from "../components/TagManagement";
import { Playbook, AccessType, UserData } from '../types';
import ToolsSelect from '../agents/ToolsSelect';
import AgentSelect from './AgentsSelect';
import ProductSelect from './ProductSelect';
import { useAuth } from '../contexts/AuthContext';
import { useUsersApi } from '../apis/users-apis';
import { Checkbox } from "../components/ui/Checkbox";
import { ADMIN_SERVICE_USER } from '../config/api-config';
import { useNavigate } from 'react-router-dom';

interface PlaybookFormProps {
  playbook: Playbook;
  originalPlaybook?: Playbook | null;
  onPlaybookChange: (playbook: Playbook) => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  readOnly?: boolean;
  onEditClick?: () => void;
  onDiscardChanges?: () => void;
  hasUnsavedChanges?: boolean;
  setIsReadOnly: (value: boolean) => void;
  tagSuggestions?: string[];
  existingPlaybooks?: Playbook[];
  isSaving?: boolean;
}

export interface PlaybookFormRef {
  validateForm: () => Promise<boolean>;
  getErrors: () => Record<string, string>;
}

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
      <span className="text-lg font-medium">Saving Playbook... Please wait</span>
    </div>
  </div>
);

const PlaybookForm = forwardRef<PlaybookFormRef, PlaybookFormProps>((props, ref) => {
  const navigate = useNavigate();
  const {
    playbook,
    onPlaybookChange,
    onSubmit,
    onCancel,
    isEditing = false,
    readOnly = false,
    onEditClick,
    onDiscardChanges,
    hasUnsavedChanges = false,
    tagSuggestions = [],
    existingPlaybooks,
    isSaving = false
  } = props;

  // Step management
  const totalSteps = 5;
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // User and admin management
  const { user } = useAuth();
  const { getUsers } = useUsersApi();
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [adminServiceUserId, setAdminServiceUserId] = useState<string>('');

  // Component refs
  const tagManagementRef = useRef<TagManagementRef>(null);
  
  // Core playbook state
  const [isCorePlaybook, setIsCorePlaybook] = useState(playbook?.isCore || false);

  // Fetch users and admin status
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const usersData = await getUsers();
        setUsers(usersData);
        
        // Check if current user is admin
        const currentUserData = usersData.find(u => u.userId === user?.sub);
        setIsCurrentUserAdmin(currentUserData?.isAdmin || false);
        
        // Find admin service account
        const adminServiceAccount = usersData.find(u => u.email === ADMIN_SERVICE_USER);
          if (adminServiceAccount) {
            setAdminServiceUserId(adminServiceAccount.userId);
          }
        } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (user?.sub) {
      fetchUsers();
    }
  }, [user?.sub]);

  // Handle core playbook changes
  useEffect(() => {
    if (isCorePlaybook && adminServiceUserId) {
      const updatedPlaybook: Playbook = {
        ...playbook,
        isCore: true,
        ownerId: adminServiceUserId,
        accessControl: {
          viewAccessType: 'Owner Only' as AccessType,
          viewAllowedUsers: [],
          editAccessType: 'Owner Only' as AccessType,
          editAllowedUsers: []
        }
      };
      onPlaybookChange(updatedPlaybook);
    } else if (!isCorePlaybook && playbook.isCore) {
      const updatedPlaybook: Playbook = {
        ...playbook,
        isCore: false
      };
      onPlaybookChange(updatedPlaybook);
    }
  }, [isCorePlaybook, adminServiceUserId]);

  // Initialize access control if not present
  useEffect(() => {
    if (!playbook.accessControl && playbook.playbookId) {
      onPlaybookChange({
        ...playbook,
        accessControl: {
          viewAccessType: 'Owner Only' as AccessType,
          viewAllowedUsers: [],
          editAccessType: 'Owner Only' as AccessType,
          editAllowedUsers: []
        }
      });
    }
  }, [playbook.playbookId]);

  const checkNameExists = useCallback((name: string): boolean => {
    if (!name || !existingPlaybooks || existingPlaybooks.length === 0) return false;
    
    return existingPlaybooks.some(existingPlaybook => {
      const isSameName = existingPlaybook?.playbookName?.toLowerCase() === name.toLowerCase();
      
      if (isEditing && playbook?.playbookId) {
        const isDifferentPlaybook = existingPlaybook?.playbookId !== playbook.playbookId;
        return isSameName && isDifferentPlaybook;
      } else {
        return isSameName;
      }
    });
  }, [existingPlaybooks, playbook?.playbookId, isEditing]);

  // Step validation functions
  const validateStep1 = useCallback((): boolean => {
      const newErrors: Record<string, string> = {};

    if (!playbook.playbookName?.trim()) {
      newErrors.playbookName = 'Playbook Name is required';
    } else if (checkNameExists(playbook.playbookName)) {
      newErrors.playbookName = 'A playbook with this name already exists';
    }
    
    if (!playbook.playbookDescription?.trim()) {
        newErrors.playbookDescription = 'Playbook Description is required';
      }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [playbook.playbookName, playbook.playbookDescription, checkNameExists]);

  const validateStep2 = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!playbook.playbookWorkflow?.trim()) {
      newErrors.playbookWorkflow = 'Workflow is required';
    }

    setErrors(prev => ({ ...prev, ...newErrors }));
      return Object.keys(newErrors).length === 0;
  }, [playbook.playbookWorkflow]);

  const validateStep3 = useCallback((): boolean => {
    // Tools are optional, so always valid
    return true;
  }, []);

  const validateStep4 = useCallback((): boolean => {
    // Tags and special instructions are optional
    return true;
  }, []);

  const validateStep5 = useCallback((): boolean => {
    // Access control validation if needed
    return true;
  }, []);

  const validateCurrentStep = useCallback(() => {
    switch (currentStep) {
      case 1: return validateStep1();
      case 2: return validateStep2();
      case 3: return validateStep3();
      case 4: return validateStep4();
      case 5: return validateStep5();
      default: return true;
    }
  }, [currentStep, validateStep1, validateStep2, validateStep3, validateStep4, validateStep5]);

  const handleNextStep = () => {
    if (validateCurrentStep()) {
      setCompletedSteps(prev => new Set([...Array.from(prev), currentStep]));
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    setCurrentStep(step);
  };

  const handleInputChange = (field: keyof Playbook, value: any) => {
    onPlaybookChange({
      ...playbook,
      [field]: value
    });

    // Clear error when user starts typing
    if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
        delete newErrors[field];
          return newErrors;
        });
    }
  };

  const handleCorePlaybookChange = (checked: boolean) => {
    setIsCorePlaybook(checked);
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Basic Information';
      case 2: return 'Workflow Configuration';
      case 3: return 'Tools & Agents';
      case 4: return 'Additional Configuration';
      case 5: return 'Access Control & Tags';
      default: return 'Configuration';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 1: return 'Set up the basic details for your playbook including name, description, and product.';
      case 2: return 'Define the workflow process and any special instructions for execution.';
      case 3: return 'Select the tools and helper agents that will be available to this playbook.';
      case 4: return 'Configure additional settings like model selection and approval requirements.';
      case 5: return 'Set access permissions and organize with tags for easy discovery.';
      default: return '';
    }
  };

  useImperativeHandle(ref, () => ({
    validateForm: async (): Promise<boolean> => {
      const step1Valid = validateStep1();
      const step2Valid = validateStep2();
      const step3Valid = validateStep3();
      const step4Valid = validateStep4();
      const step5Valid = validateStep5();
      
      return step1Valid && step2Valid && step3Valid && step4Valid && step5Valid;
    },
    getErrors: () => errors
  }));

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Playbook Name <span className="text-red-500">*</span>
                </label>
          <Input
                  value={playbook.playbookName || ''}
            onChange={(e) => handleInputChange('playbookName', e.target.value)}
            placeholder="Enter playbook name"
            className={errors.playbookName ? 'border-red-500' : ''}
          />
                {errors.playbookName && (
                  <p className="mt-1 text-sm text-red-600">{errors.playbookName}</p>
                )}
              </div>

                             <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Product Name
                 </label>
          <ProductSelect
                   selectedProduct={playbook.playbookProductName || ''}
                   onChange={(value) => handleInputChange('playbookProductName', value)}
                 />
               </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
          <Textarea
                  value={playbook.playbookDescription || ''}
            onChange={(e) => handleInputChange('playbookDescription', e.target.value)}
                  placeholder="Describe what this playbook does and its purpose"
            rows={4}
            className={errors.playbookDescription ? 'border-red-500' : ''}
          />
                {errors.playbookDescription && (
                  <p className="mt-1 text-sm text-red-600">{errors.playbookDescription}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Model
                </label>
          <Select
                  value={playbook.playbookModel || 'gpt-4o'}
            onValueChange={(value) => handleInputChange('playbookModel', value)}
          >
            <SelectTrigger>
                    <SelectValue placeholder="Select AI model" />
            </SelectTrigger>
            <SelectContent>
                    <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
              <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                    <SelectItem value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</SelectItem>
                    <SelectItem value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</SelectItem>
            </SelectContent>
          </Select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow <span className="text-red-500">*</span>
              </label>
          <Textarea
                value={playbook.playbookWorkflow || ''}
            onChange={(e) => handleInputChange('playbookWorkflow', e.target.value)}
                placeholder="Define the step-by-step workflow process for this playbook"
                rows={8}
            className={errors.playbookWorkflow ? 'border-red-500' : ''}
          />
              {errors.playbookWorkflow && (
                <p className="mt-1 text-sm text-red-600">{errors.playbookWorkflow}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Special Instructions
              </label>
          <Textarea
                value={playbook.playbookSpecialInstructions || ''}
            onChange={(e) => handleInputChange('playbookSpecialInstructions', e.target.value)}
                placeholder="Any special instructions or guidelines for execution"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Approval Notes
              </label>
          <Textarea
                value={playbook.playbookApprovalNotes || ''}
            onChange={(e) => handleInputChange('playbookApprovalNotes', e.target.value)}
                placeholder="Notes about approval requirements or processes"
            rows={3}
          />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
                         <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Active Tools
               </label>
          <ToolsSelect
                 selectedTools={playbook.playbookActiveTools || []}
            onChange={(tools) => handleInputChange('playbookActiveTools', tools)}
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Approval Tools
               </label>
               <ToolsSelect
                 selectedTools={playbook.playbookApprovalTools || []}
                 onChange={(tools) => handleInputChange('playbookApprovalTools', tools)}
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Helper Agents
               </label>
          <AgentSelect
                 selectedAgents={playbook.playbookHelperAgents || []}
            onChange={(agents) => handleInputChange('playbookHelperAgents', agents)}
               />
             </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chat Type
              </label>
              <Select
                value={playbook.playbookChatType || ''}
                onValueChange={(value) => handleInputChange('playbookChatType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select chat type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard Chat</SelectItem>
                  <SelectItem value="structured">Structured Chat</SelectItem>
                  <SelectItem value="workflow">Workflow Chat</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isCurrentUserAdmin && (
              <div className="flex items-center space-x-2">
            <Checkbox
              id="core-playbook"
              checked={isCorePlaybook}
              onCheckedChange={handleCorePlaybookChange}
            />
                <label htmlFor="core-playbook" className="text-sm font-medium text-gray-700">
                  Mark as Core Playbook
              </label>
          </div>
        )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <TagManagement
                ref={tagManagementRef}
                tags={playbook.playbookTags || []}
                onChange={(tags) => handleInputChange('playbookTags', tags)}
                suggestions={tagSuggestions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                View Access
              </label>
                             <Select
                 value={playbook.accessControl?.viewAccessType || 'Owner Only'}
                 onValueChange={(value) => handleInputChange('accessControl', {
                   ...playbook.accessControl,
                   viewAccessType: value,
                   viewAllowedUsers: value === 'Owner Only' ? [] : playbook.accessControl?.viewAllowedUsers || []
                 })}
                 disabled={isCorePlaybook}
               >
                 <SelectTrigger>
                   <SelectValue />
                 </SelectTrigger>
                 <SelectContent>
                   <SelectItem value="Owner Only">Owner Only</SelectItem>
                   <SelectItem value="All Users">All Users</SelectItem>
                   <SelectItem value="Allowed Users">Allowed Users</SelectItem>
                 </SelectContent>
               </Select>
             </div>

             {playbook.accessControl?.viewAccessType === 'Allowed Users' && !isCorePlaybook && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed Viewers
                </label>
                <MultiSelect
                  options={users.map(user => ({ value: user.userId, label: user.email }))}
                  value={playbook.accessControl?.viewAllowedUsers || []}
                  onChange={(selectedUsers) => handleInputChange('accessControl', {
                    ...playbook.accessControl,
                    viewAllowedUsers: selectedUsers
                  })}
                  placeholder="Select users who can view this playbook"
                />
          </div>
        )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Edit Access
              </label>
                             <Select
                 value={playbook.accessControl?.editAccessType || 'Owner Only'}
                 onValueChange={(value) => handleInputChange('accessControl', {
                   ...playbook.accessControl,
                   editAccessType: value,
                   editAllowedUsers: value === 'Owner Only' ? [] : playbook.accessControl?.editAllowedUsers || []
                 })}
                 disabled={isCorePlaybook}
               >
                 <SelectTrigger>
                   <SelectValue />
                 </SelectTrigger>
                 <SelectContent>
                   <SelectItem value="Owner Only">Owner Only</SelectItem>
                   <SelectItem value="All Users">All Users</SelectItem>
                   <SelectItem value="Allowed Users">Allowed Users</SelectItem>
                 </SelectContent>
               </Select>
              </div>

             {playbook.accessControl?.editAccessType === 'Allowed Users' && !isCorePlaybook && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed Editors
                </label>
                <MultiSelect
                  options={users.map(user => ({ value: user.userId, label: user.email }))}
                  value={playbook.accessControl?.editAllowedUsers || []}
                  onChange={(selectedUsers) => handleInputChange('accessControl', {
                    ...playbook.accessControl,
                    editAllowedUsers: selectedUsers
                  })}
                  placeholder="Select users who can edit this playbook"
                />
              </div>
            )}
        </div>
        );

      default:
        return null;
    }
  };

  if (readOnly) {
    // For read-only mode, we'll keep the existing view structure but with updated mobile responsiveness
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-cyan-50">
        <div className="container mx-auto p-4 sm:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8">
            <div className="mb-4 sm:mb-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-gradient-to-r from-sky-500 to-cyan-500 rounded-lg">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {playbook.playbookName || 'Playbook Details'}
                </h1>
              </div>
              <p className="text-sm sm:text-base text-gray-600">
                View and manage this playbook configuration
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/studio/chat`, { replace: true, state: { playbookId: playbook.playbookId } })}
                className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Chat with Agent</span>
                <span className="sm:hidden">Chat</span>
              </Button>
              {onEditClick && (
              <Button
                onClick={onEditClick}
                  className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white"
                  size="sm"
              >
                  <Settings className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Edit Playbook</span>
                  <span className="sm:hidden">Edit</span>
              </Button>
            )}
            <Button
                onClick={onCancel}
              variant="outline"
              size="sm"
                className="border-sky-200 text-sky-600 hover:bg-sky-50"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Back to Playbooks</span>
                <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>

          {/* Content - implement view mode here similar to ViewPlaybookPage */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Description</label>
                    <p className="text-sm sm:text-base text-gray-900 break-words">{playbook.playbookDescription || 'No description provided'}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Product</label>
                    <p className="text-sm sm:text-base text-gray-900">{playbook.playbookProductName || 'No product specified'}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Model</label>
                    <p className="text-sm sm:text-base text-gray-900">{playbook.playbookModel || 'gpt-4o'}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Workflow</h3>
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-xs sm:text-sm whitespace-pre-wrap border">
                  {playbook.playbookWorkflow || 'No workflow defined'}
                </div>
              </Card>

              {playbook.playbookSpecialInstructions && (
                <Card className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Special Instructions</h3>
                  <div className="bg-gray-50 rounded-lg p-4 text-xs sm:text-sm whitespace-pre-wrap border">
                    {playbook.playbookSpecialInstructions}
                  </div>
                </Card>
              )}
            </div>

            {/* Right Column - Metadata */}
            <div className="space-y-6">
              <Card className="p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Details</h3>
                <div className="space-y-3 text-xs sm:text-sm">
                  <div>
                    <span className="text-gray-500">Playbook ID:</span>
                    <p className="font-mono bg-gray-50 px-2 py-1 rounded text-xs break-all">
                      {playbook.playbookId}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Chat Type:</span>
                    <p className="text-gray-900">{playbook.playbookChatType || 'Not specified'}</p>
                  </div>
                  {playbook.isCore && (
                    <div>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                        Core Playbook
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-cyan-50">
      {isSaving && <LoadingOverlay />}
      
      <div className="container mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-r from-sky-500 to-cyan-500 rounded-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit Playbook' : 'Create New Playbook'}
              </h1>
            </div>
            <p className="text-sm sm:text-base text-gray-600">
              {getStepDescription()}
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/studio/chat`, { replace: true, state: { playbookId: playbook.playbookId } })}
              className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Chat with Agent</span>
              <span className="sm:hidden">Chat</span>
            </Button>
            <Button
              onClick={onCancel}
              variant="outline"
              size="sm"
              className="border-sky-200 text-sky-600 hover:bg-sky-50 w-full sm:w-auto"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Back to Playbooks</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-0">
              Step {currentStep} of {totalSteps}: {getStepTitle()}
            </h2>
            <div className="text-xs sm:text-sm text-gray-500">
              Progress: {Math.round((completedSteps.size / totalSteps) * 100)}%
            </div>
          </div>
          
          <div className="flex space-x-2 sm:space-x-4 overflow-x-auto pb-2">
            {Array.from({ length: totalSteps }, (_, i) => {
              const stepNum = i + 1;
              const isCompleted = completedSteps.has(stepNum);
              const isCurrent = stepNum === currentStep;
              const isAccessible = stepNum <= currentStep || isCompleted;

              const getStepLabel = (stepNum: number) => {
                switch (stepNum) {
                  case 1: return { icon: Info, label: 'Basic' };
                  case 2: return { icon: Workflow, label: 'Workflow' };
                  case 3: return { icon: Zap, label: 'Tools' };
                  case 4: return { icon: Settings, label: 'Config' };
                  case 5: return { icon: User, label: 'Access' };
                  default: return { icon: Info, label: 'Step' };
                }
              };

              const { icon: Icon, label } = getStepLabel(stepNum);

              return (
                <button
                  key={stepNum}
                  onClick={() => isAccessible && handleStepClick(stepNum)}
                  disabled={!isAccessible}
                  className={`
                    flex items-center space-x-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap
                    ${isCurrent 
                      ? 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg' 
                      : isCompleted 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : isAccessible
                          ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  {isCompleted ? (
                    <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                  ) : (
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{stepNum}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <Card className="mb-6">
          <CardContent className="p-4 sm:p-6">
            {renderStepContent()}
      </CardContent>
    </Card>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex gap-2">
            {currentStep > 1 && (
          <Button
                onClick={handlePreviousStep}
                variant="outline"
                className="border-sky-200 text-sky-600 hover:bg-sky-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
                Previous
          </Button>
            )}
        </div>
          
          <div className="flex gap-2">
            {hasUnsavedChanges && onDiscardChanges && (
              <Button
                onClick={onDiscardChanges}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={isSaving}
              >
                Discard Changes
              </Button>
            )}
            
            {currentStep < totalSteps ? (
              <Button
                onClick={handleNextStep}
                className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white"
              >
                Next Step
                <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
              </Button>
            ) : (
              <Button
                onClick={onSubmit}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    {isEditing ? 'Update Playbook' : 'Create Playbook'}
                  </>
                )}
              </Button>
            )}
      </div>
      </div>
      </div>
    </div>
  );
});

export default PlaybookForm;